import classNames from 'classnames';
import {useMediaQuery} from 'hooks/useMediaQuery';
import {breakpoints} from 'lib/breakpoints';
import {formatMoney} from 'lib/formatters';
import {getRegionalPhone} from 'lib/getters';
import {unicodeSymbols} from 'lib/unicodeSymbols';
import {CartStep} from 'modules/Cart/contexts/cartContext';
import {FC, useEffect, useRef, useState} from 'react';

import {Button, ButtonSize, ButtonType} from 'components/Button/Button';
import {Icon} from 'components/Icon/Icon';
import {Text} from 'components/Text/Text';

import styles from './CartSidebar.module.css';

type Props = {
    className?: string;
    productsTypeAmount: number;
    totalPriceOfProducts: number;
    totalPrice: number;
    totalProductsAmount: number;
    totalDiscount: number;
    regionalDiscounts: number;
    minimalPrice: number;
    cartHasRegionalDiscount: boolean;
    shouldShowFormError?: boolean;
    step: CartStep;
    isSubmittedMode?: boolean;
    submitButtonIsDisabled?: boolean;
    priceIsTooLow?: boolean;
    handleSubmit?: () => void;
    handleCallback: () => void;
    orderId?: string;
};
export const CartSidebar: FC<Props> = ({
    className,
    productsTypeAmount,
    totalProductsAmount,
    totalPriceOfProducts,
    totalPrice,
    totalDiscount,
    minimalPrice,
    regionalDiscounts,
    cartHasRegionalDiscount,
    isSubmittedMode,
    submitButtonIsDisabled,
    shouldShowFormError,
    priceIsTooLow,
    step,
    orderId,
    handleSubmit,
    handleCallback,
}) => {
    const footerRef = useRef<HTMLDivElement>(null);
    const shouldShowPdfLink = true;

    const isTablet = useMediaQuery(breakpoints.up('sm'));
    const isDesktop = useMediaQuery(breakpoints.up('mmd'));

    const [fixedPanelIsVisible, setFixedPanelIsVisible] = useState(false);

    useEffect(() => {
        function checkPosition() {
            if (!footerRef.current) {
                return;
            }

            const bounds = footerRef.current.getBoundingClientRect();
            if (bounds.top - window.innerHeight > 0) {
                setFixedPanelIsVisible(true);
            } else {
                setFixedPanelIsVisible(false);
            }
        }

        checkPosition();

        document.addEventListener('scroll', checkPosition);

        return () => {
            document.removeEventListener('scroll', checkPosition);
        };
    }, []);

    return (
        <div
            className={classNames(styles.block, className, {
                [styles.orderSuccessMode]: isSubmittedMode,
            })}
        >
            <div className={styles.main}>
                <Text.Bold20 className={styles.title}>Ваш заказ</Text.Bold20>
                <div className={styles.group}>
                    <Text.Bold14 className={styles.label}>
                        {productsTypeAmount} позиции
                    </Text.Bold14>
                    <Text.Bold14 className={styles.text}>
                        {formatMoney(totalPriceOfProducts)}
                    </Text.Bold14>
                    <Text.Regular14 className={styles.sublabel}>
                        ({totalProductsAmount} товаров)
                    </Text.Regular14>
                </div>
                {Boolean(totalDiscount) && (
                    <div className={styles.group}>
                        <Text.Bold14 className={styles.label}>
                            Скидка на товары
                        </Text.Bold14>
                        <Text.Bold14 className={styles.discount}>
                            {unicodeSymbols.ndash}
                            {formatMoney(totalDiscount)}
                        </Text.Bold14>
                    </div>
                )}
                {cartHasRegionalDiscount && regionalDiscounts !== 1 && (
                    <div className={styles.group}>
                        <Text.Bold14 className={styles.label}>
                            Скидка интернет-магазина
                        </Text.Bold14>
                        <Text.Bold14 className={styles.discount}>
                            {Math.round((1 - regionalDiscounts) * 100) + '%'}
                        </Text.Bold14>
                    </div>
                )}
                <div className={styles.total}>
                    <Text.Bold14 className={styles.label}>Итого</Text.Bold14>
                    <Text.Responsive
                        breakpointsMap={{default: 'Bold20', sm: 'Bold24'}}
                        className={styles.price}
                    >
                        {formatMoney(totalPrice)}
                    </Text.Responsive>
                </div>
            </div>

            <div className={styles.secondary}>
                <div className={styles.footer} ref={footerRef}>
                    {!isSubmittedMode && (
                        <Button
                            size={ButtonSize.L}
                            className={styles.submitButton}
                            onClick={handleSubmit}
                            disabled={submitButtonIsDisabled}
                        >
                            {step === CartStep.Products &&
                                'Перейти к оформлению'}
                            {step === CartStep.Order && 'Оформить заказ'}
                        </Button>
                    )}
                    {shouldShowFormError && (
                        <div className={styles.errorLabel}>
                            <Icon name="info19" className={styles.errorIcon} />
                            <Text.Bold14 className={styles.errorText}>
                                Пожалуйста, заполните все поля со звездочкой
                            </Text.Bold14>
                        </div>
                    )}
                    {priceIsTooLow && (
                        <div className={styles.warnLabel}>
                            <Icon name="info" className={styles.warnIcon} />
                            <Text.Bold14 className={styles.warnText}>
                                Минимальная сумма заказа в вашем регионе —{' '}
                                {formatMoney(minimalPrice)}
                            </Text.Bold14>
                        </div>
                    )}
                    {shouldShowPdfLink && (
                        <button
                            className={classNames(styles.pdfLink, 'js-modal')}
                            type="button"
                            data-modal-settings={JSON.stringify({
                                pdf: {
                                    service: '/pdf_external_service.php',
                                    generator: orderId
                                        ? `/shop/cart/info/${orderId}/?download`
                                        : '/shop/cart/?download',
                                },
                            })}
                            data-modal-url="/static/km2/build/modal/pdf.html"
                        >
                            <Icon name="doc" />
                            <Text.Bold14>Скачать заказ (PDF)</Text.Bold14>
                        </button>
                    )}
                </div>

                <div className={styles.contacts}>
                    <Text.Regular14 className={styles.contactsText}>
                        Вы можете оформить заказ или получить консультацию
                        по телефону
                    </Text.Regular14>
                    <Button
                        component="a"
                        link={`tel:${getRegionalPhone()}`}
                        type={
                            isTablet
                                ? ButtonType.SecondaryOutline
                                : ButtonType.PrimaryOutline
                        }
                        iconName="phone"
                        className={styles.contactsButton}
                    >
                        {getRegionalPhone()}
                    </Button>
                    <Text.Bold16
                        as="a"
                        href={`tel:${getRegionalPhone()}`}
                        className={styles.contactsPhone}
                    >
                        {getRegionalPhone()}
                    </Text.Bold16>
                    <Button
                        type={ButtonType.SecondaryOutline}
                        iconName={isDesktop ? 'phone' : undefined}
                        className={styles.callbackButton}
                        onClick={handleCallback}
                    >
                        Заказать звонок
                    </Button>
                </div>

                <div className={styles.support}>
                    <Text.Regular14 className={styles.supportTitle}>
                        Телефон для общих вопросов
                    </Text.Regular14>
                    <a href={`tel:${getRegionalPhone()}`}>
                        <Text.Bold16 className={styles.supportPhone}>
                            {getRegionalPhone()}
                        </Text.Bold16>
                    </a>
                    <Text.Regular14 className={styles.supportLabel}>
                        Бесплатно по России
                    </Text.Regular14>
                </div>
            </div>

            {!isSubmittedMode && (
                <div
                    className={classNames(styles.fixedPanel, {
                        [styles.visible]: fixedPanelIsVisible,
                    })}
                >
                    <Button
                        size={ButtonSize.M}
                        className={styles.submitButton}
                        onClick={handleSubmit}
                        disabled={submitButtonIsDisabled}
                    >
                        {step === CartStep.Products && 'Перейти к оформлению'}
                        {step === CartStep.Order && 'Оформить заказ'}
                    </Button>

                    {shouldShowFormError && (
                        <div className={styles.errorLabel}>
                            <Icon name="info19" className={styles.errorIcon} />
                            <Text.Bold14 className={styles.errorText}>
                                Пожалуйста, заполните все поля со звездочкой
                            </Text.Bold14>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};
